import { FiChevronRight, FiFilter } from "solid-icons/fi";
import { createSignal, type JSX, type ParentComponent, Show } from "solid-js";
import { Container } from "../shared/Container";
import { useLayoutContext } from "./context/LayoutContext";

type ActiveSearchLayoutProps = {
  children: JSX.Element;
  sidebar?: JSX.Element;
  storageKey?: string;
  /**
   * Drop the built-in `lg:ml-16` left offset (the nav-rail clearance). Set this when
   * the layout is nested inside a host that already applies the left offset —
   * e.g. `CompanySidebarLayout` → `MainContentContainer` — so the content isn't
   * indented twice and stays aligned with the host's own header.
   */
  disableLeftOffset?: boolean;
};

const DEFAULT_STORAGE_KEY = "sidebar_active_search_filters";

export const ActiveSearchLayout = (props: ActiveSearchLayoutProps) => {
  const storageKey = props.storageKey ?? DEFAULT_STORAGE_KEY;

  const [isRightCollapsed, setIsRightCollapsed] = createSignal(
    localStorage.getItem(storageKey) === "true",
  );

  const toggle = () => {
    const next = !isRightCollapsed();
    setIsRightCollapsed(next);
    localStorage.setItem(storageKey, String(next));
  };

  return (
    <>
      <Show when={props.sidebar}>
        {/* Expanded: fixed right sidebar. Stays mounted so it can animate out —
            Solid's <Show> unmounts instantly and kills the exit. */}
        <aside
          class="fixed inset-y-0 right-0 lg:top-14 top-12 z-20 bg-white border-l border-gray-200 w-80 hidden lg:flex flex-col transition-[translate] duration-200 ease-drawer motion-reduce:transition-none"
          classList={{
            "translate-x-0": !isRightCollapsed(),
            "translate-x-full": isRightCollapsed(),
          }}
          inert={isRightCollapsed() || undefined}
        >
          {/* Close toggle on the left edge */}
          <button
            type="button"
            onClick={toggle}
            class="hidden lg:flex absolute -left-3 top-6 w-6 h-6 bg-white border border-gray-200 rounded-full items-center justify-center shadow-sm hover:bg-gray-50 transition-colors z-10"
            aria-label="Collapse filters"
          >
            <FiChevronRight class="w-3.5 h-3.5 text-gray-500" />
          </button>

          <div class="flex-1 overflow-hidden">{props.sidebar}</div>
        </aside>

        {/* Collapsed: floating button */}
        <button
          type="button"
          onClick={toggle}
          aria-label="Open filters"
          class="hidden lg:flex fixed right-6 top-20 z-20 items-center gap-2 px-4 h-10 bg-white border border-gray-200 rounded-full shadow-md hover:shadow-lg hover:bg-gray-50 text-sm font-medium text-gray-700 transition-[box-shadow,background-color,opacity] duration-200 ease-drawer motion-reduce:transition-none"
          classList={{
            "opacity-100": isRightCollapsed(),
            "opacity-0 pointer-events-none": !isRightCollapsed(),
          }}
          inert={!isRightCollapsed() || undefined}
        >
          <FiFilter class="w-4 h-4 shrink-0" />
          Filtri
        </button>
      </Show>

      {/* Main content */}
      <main
        class="flex-1 pb-6 min-h-screen transition-[margin] duration-200 ease-drawer motion-reduce:transition-none"
        classList={{
          // Both gutters are clearance for chrome that only exists from `lg` up:
          // the nav rail is `-translate-x-full` below it, and the floating
          // "Filtri" button is `hidden lg:flex`. Ungated they reserved 144px of
          // dead space on a 375px screen, and since the ancestor `<main>` is a
          // flex item with `min-width: auto` the document widened to 543px
          // instead of shrinking — which is what dragged the fixed-position
          // navbar past the edge of the viewport.
          "lg:ml-16": !props.disableLeftOffset,
          "lg:mr-80": !isRightCollapsed() && !!props.sidebar,
        }}
      >
        <Container size="full" class="ml-0 lg:mr-20">
          {props.children}
        </Container>
      </main>
    </>
  );
};

export const ActiveSearchLayoutSkeleton: ParentComponent = (props) => {
  const { isSidebarCollapsed } = useLayoutContext();
  return (
    <main
      class="flex-1 pt-20 lg:pt-24 pb-6 min-h-screen"
      classList={{
        "lg:ml-64": !isSidebarCollapsed(),
        "lg:ml-16": isSidebarCollapsed(),
      }}
    >
      {props.children}
    </main>
  );
};
